import {useQueryClient} from '@tanstack/react-query';
import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';

import {marketDistributorWithDistributorT} from '@/lib/types/marketDistributor.types';
import {retailerT} from '@/lib/types/retailer.types';

import {SupabaseInstance} from '@/lib/utils';

import {useDataQuery} from '@/hooks';

import {useNotifications} from '@/providers/components/notificationsProvider';

type OrderItemT = {
  quantity: number;
  product_name: string | null;
  fk_distributor_id: number | null;
  distributor_name: string | null;
  item_count: number;
  product_logo?: string | null;
  brand_logo?: string | null;
  brand_name: string | null;
  fk_product_id: number;
};

type createRetailOrderContextT = {
  // State
  error: string;
  isSubmitting: boolean;
  acknowledged: boolean;
  acknowledging: boolean;
  orderItems: {[key: number]: OrderItemT};
  searchTerm: string;
  markets: {fk_market_id: number; Market: {market_name: string}}[];
  distributors: marketDistributorWithDistributorT[];
  retailer: retailerT | null;
  selectedDistributorId: number | null;
  selectedProductIds: Set<number>;

  // Functions
  setAcknowledged: (acknowledged: boolean) => void;
  setAcknowledging: (acknowledged: boolean) => void;
  resetOrderItems: () => void;
  handleSubmitOrder: () => Promise<void>;
  setSearchTerm: (term: string) => void;
  updateOrderItem: (productId: number, item: OrderItemT) => void;
  removeOrderItem: (productId: number) => void;
  getSelectedDistributor: () => marketDistributorWithDistributorT | null;
};

const CreateRetailOrderContext = createContext<createRetailOrderContextT | undefined>(undefined);

export const useCreateRetailOrder = () => {
  const context = useContext(CreateRetailOrderContext);
  if (!context) {
    throw new Error('useCreateOrder must be used within a CreateOrderProvider');
  }
  return context;
};

interface OrderDataProviderProps {
  children: ReactNode;
  retailerId: number;
}

export const CreateRetailOrderProvider: React.FC<OrderDataProviderProps> = ({children, retailerId}) => {
  // Hooks
  const queryClient = useQueryClient();
  const {toast} = useNotifications();

  // State
  const [retailer, setRetailer] = useState<retailerT | null>(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [orderItems, setOrderItems] = useState<{[key: number]: OrderItemT}>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDistributorId, setSelectedDistributorId] = useState<number | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({queryKey: ['Order']});
    await queryClient.refetchQueries({queryKey: ['Order']});
  };

  // Fetch retailer data
  useEffect(() => {
    const fetchRetailer = async () => {
      if (!retailerId) return;

      const {data, error} = await SupabaseInstance.supabase
        .from('Retailer')
        .select('*')
        .eq('retailer_id', retailerId)
        .single();

      if (error) {
        console.error('Error fetching retailer:', error);
        toast.error('Failed to fetch retailer details');
        return;
      }

      setRetailer(data);
    };

    fetchRetailer();
  }, [retailerId, toast]);

  // Markets in Retailer Hook
  const {data: {data: markets} = {data: []}} = useDataQuery({
    table: 'Market_Retailer',
    columns: ['fk_market_id', ['Market', ['market_name']]],
    limit: 10,
    searchTerm,
    searchColumn: 'Market.market_name',
    filters: [
      {
        column: 'fk_retailer_id',
        operator: 'eq',
        value: retailer?.retailer_id,
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!retailer,
  });

  // Distributors in Markets Hook
  const {data: {data: distributors} = {data: []}} = useDataQuery({
    table: 'Market_Distributor',
    columns: ['*', ['Distributor', ['*']]],
    limit: 10,
    searchTerm,
    searchColumn: 'Distributor.distributor_name',
    filters: [
      {
        column: 'fk_market_id',
        operator: 'in',
        value: markets.map(m => m.fk_market_id),
      },
      {
        column: 'date_removed',
        operator: 'is',
        value: null,
      },
    ],
    enabled: !!markets,
  });

  const resetOrderItems = () => {
    setOrderItems({});
  };

  const handleSubmitOrder = async () => {
    const {data: user, error: userError} = await SupabaseInstance.supabase.auth.getUser();

    if (userError) {
      console.error('Error fetching user:', userError);
      toast.error('Failed to fetch user. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const {data, error} = await SupabaseInstance.supabase.rpc('create_retailer_order', {
        p_order_items: orderItems,
        p_retailer_id: retailerId,
        user_uid: user.user.id,
      });

      if (error) throw error;

      if (data) {
        toast.success('Order created successfully!');
        // Reset the form or navigate to a new page
        resetOrderItems();
        invalidateQuery();
      } else {
        toast.error('Failed to create order. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Error submitting order in provider:', error);
      setError('An error occurred while submitting the order. Please try again.');
      if (error instanceof Error) {
        toast.error(`Failed to submit order: ${error.message}`);
      }
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateOrderItem = (productId: number, item: OrderItemT) => {
    if (item.quantity > 0) {
      // If this is the first item, set the distributor
      if (Object.keys(orderItems).length === 0) {
        setSelectedDistributorId(item.fk_distributor_id);
      }

      setOrderItems(prev => ({...prev, [productId]: item}));
      setSelectedProductIds(prev => new Set([...prev, productId]));
    } else {
      removeOrderItem(productId);
    }
  };

  const removeOrderItem = (productId: number) => {
    setOrderItems(prev => {
      const newItems = {...prev};
      delete newItems[productId];

      // If no items left, reset distributor
      if (Object.keys(newItems).length === 0) {
        setSelectedDistributorId(null);
      }

      return newItems;
    });
    setSelectedProductIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(productId);
      return newSet;
    });
  };

  const getSelectedDistributor = () => {
    return distributors.find(d => d.Distributor.distributor_id === selectedDistributorId) || null;
  };

  const value = {
    // State
    error,
    isSubmitting,
    acknowledging,
    acknowledged,
    orderItems,
    searchTerm,
    markets,
    distributors,
    retailer,
    selectedDistributorId,
    selectedProductIds,

    // Functions
    setAcknowledging,
    setAcknowledged,
    resetOrderItems,
    handleSubmitOrder,
    setSearchTerm,
    updateOrderItem,
    removeOrderItem,
    getSelectedDistributor,
  };

  return <CreateRetailOrderContext.Provider value={value}>{children}</CreateRetailOrderContext.Provider>;
};
